import { useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Aba {
  rotulo: string;
  rota: string;
}

/** F6: kicker, título, botão primário rosa, abas da seção (submenus vivem aqui) e conteúdo. */
export function PaginaSecao({ kicker, titulo, acao, abas, children }: { kicker: string; titulo: string; acao?: ReactNode; abas?: Aba[]; children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-8 sm:py-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker mb-2">{kicker}</p>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{titulo}</h1>
        </div>
        {acao && <div data-tour="acao">{acao}</div>}
      </div>
      {abas && abas.length > 0 && <LinhaDeAbas titulo={titulo} abas={abas} />}
      {children}
    </div>
  );
}

/**
 * F6 e VIS-07: abas da seção. Quando não cabem, a linha rola e setas aparecem do lado que tem abas escondidas
 * (sem máscara de cor; P11). A aba ativa é trazida para a vista.
 */
function LinhaDeAbas({ titulo, abas }: { titulo: string; abas: Aba[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  const [lados, setLados] = useState({ esq: false, dir: false });
  const [transborda, setTransborda] = useState(false);
  // VA-R26-01 (rodada 26): abas que não cabem inteiras entre as setas ficam transparentes (sem pedaço de rótulo solto);
  // continuam focáveis, e o foco as traz para a vista.
  const [parciais, setParciais] = useState<ReadonlySet<string>>(new Set());
  // VB4-01 e VA4-02 (rodada 4): o padding das setas é fixo enquanto a linha transborda (não muda com a rolagem), a
  // posição vem do retângulo real e o ajuste roda a cada troca de rota e de tamanho, depois do padding aplicado.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      const transb = el.scrollWidth > el.clientWidth + 2;
      const esq = el.scrollLeft > 2;
      const dir = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setTransborda(transb);
      setLados({ esq, dir });
      const n = el.getBoundingClientRect();
      const lim = { de: n.left + (esq ? 32 : 0), ate: n.right - (dir ? 32 : 0) };
      const fora = new Set<string>();
      if (transb) for (const a of el.querySelectorAll<HTMLElement>('a[data-rota]')) {
        const r = a.getBoundingClientRect();
        if (r.left < lim.de - 1 || r.right > lim.ate + 1) fora.add(a.dataset.rota!);
      }
      setParciais((antes) => (antes.size === fora.size && [...fora].every((x) => antes.has(x)) ? antes : fora));
    };
    const posicionar = () => {
      const ativa = el.querySelector<HTMLElement>('[aria-current="page"]');
      if (ativa) {
        const folga = 44; // seta de 32px mais respiro (espaçador de 40px nas pontas)
        const n = el.getBoundingClientRect();
        const a = ativa.getBoundingClientRect();
        if (a.left < n.left + folga) el.scrollLeft -= n.left + folga - a.left;
        else if (a.right > n.right - folga) el.scrollLeft += a.right - (n.right - folga);
      }
      medir();
    };
    posicionar();
    const ro = new ResizeObserver(posicionar);
    ro.observe(el);
    el.addEventListener('scroll', medir, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', medir);
    };
  }, [abas, pathname, transborda]);
  const rolar = (d: number) => ref.current?.scrollBy({ left: d * Math.max(160, (ref.current?.clientWidth ?? 0) * 0.6), behavior: 'smooth' });
  const seta = 'absolute top-0 bottom-px z-10 grid w-8 place-items-center border-borda bg-fundo text-tinta-2 hover:text-tinta-1';
  return (
    <div className="relative -mx-4 mb-6 sm:mx-0">
      {lados.esq && (
        <button type="button" onClick={() => rolar(-1)} aria-label={t('abas.anteriores')} className={`${seta} left-0 border-r`}>
          <ChevronLeft size={16} strokeWidth={1.75} />
        </button>
      )}
      <nav ref={ref} className={`flex gap-1 overflow-x-auto border-b border-borda ${transborda ? '' : 'px-4 sm:px-0'}`} aria-label={titulo}>
          {/* VB-R26-03 (rodada 26): espaçadores reais no começo e no fim; o padding do contêiner rolável não garantia
              a folga da seta na última aba. */}
          {transborda && <span aria-hidden className="w-10 shrink-0" />}
          {abas.map((a) => (
            <NavLink
              key={a.rota}
              to={a.rota}
              end
              data-rota={a.rota}
              // VB11-02 e VA10-02 (rodadas 10 e 11): a aba focada pelo teclado vem para a área visível, fora das setas, e o
              // anel de foco fica dentro dela (a linha rola na horizontal e recortava o anel por cima e por baixo).
              onFocus={(e) => {
                const nav = ref.current;
                if (!nav) return;
                const n = nav.getBoundingClientRect();
                const a = e.currentTarget.getBoundingClientRect();
                if (a.left < n.left + 44) nav.scrollLeft -= n.left + 44 - a.left;
                else if (a.right > n.right - 44) nav.scrollLeft += a.right - (n.right - 44);
              }}
              className={({ isActive }) =>
                `${parciais.has(a.rota) ? 'opacity-0 focus-visible:opacity-100 ' : ''}-mb-px shrink-0 rounded-t-md border-b-2 px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-acento ${isActive ? 'border-acento font-medium text-tinta-1' : 'border-transparent text-tinta-3 hover:text-tinta-1'}`
              }
            >
              {a.rotulo}
            </NavLink>
          ))}
          {transborda && <span aria-hidden className="w-10 shrink-0" />}
      </nav>
      {lados.dir && (
        <button type="button" onClick={() => rolar(1)} aria-label={t('abas.seguintes')} className={`${seta} right-0 border-l`}>
          <ChevronRight size={16} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

export function BotaoPrimario(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex h-10 items-center gap-2 rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte disabled:opacity-50 ${props.className ?? ''}`}
    />
  );
}
